// Toasts (design system: confirm what just happened, offer undo where possible). A toast with
// somewhere to go stays longer and carries an Open link (30-day list row 17: every act confirms
// itself with what happened, to whom, and a way to the record).
import { useEffect, useState } from 'react';
import { Link } from 'react-router';

export interface Toast { id: number; text: string; kind?: 'ok' | 'crit' | 'info'; action?: { label: string; run: () => void }; href?: string | null; hrefLabel?: string }
type Listener = (t: Toast[]) => void;
let toasts: Toast[] = [];
const listeners = new Set<Listener>();
let seq = 1;

export function toast(text: string, kind: Toast['kind'] = 'ok', action?: Toast['action']) {
  push({ id: seq++, text, kind, action });
}
/** A confirmation that can be opened: "RFI-004 sent to Paul · 2 files" with an Open link to the record or the mail entry. */
export function done(text: string, opts: { href?: string | null; label?: string; kind?: Toast['kind'] } = {}) {
  push({ id: seq++, text, kind: opts.kind ?? 'ok', href: opts.href ?? null, hrefLabel: opts.label ?? 'Open' });
}
function push(t: Toast) {
  toasts = [...toasts, t];
  listeners.forEach(l => l(toasts));
  setTimeout(() => dismiss(t.id), t.action || t.href ? 9000 : 4000);
}
function dismiss(id: number) { toasts = toasts.filter(t => t.id !== id); listeners.forEach(l => l(toasts)); }

export function Toaster() {
  const [list, setList] = useState<Toast[]>(toasts);
  useEffect(() => { listeners.add(setList); return () => { listeners.delete(setList); }; }, []);
  if (!list.length) return null;
  return (
    <div className="fixed inset-x-3 z-50 flex flex-col gap-2 pointer-events-none" style={{ bottom: 'calc(84px + env(safe-area-inset-bottom, 0px))' }} aria-live="polite">
      {list.map(t => (
        <div key={t.id} className="pointer-events-auto flex items-center gap-2.5 bg-fg text-bg rounded-[12px] px-3.5 py-3 font-semibold text-sm shadow-lg mx-auto w-full max-w-[520px]">
          <span className={`w-2 h-2 rounded-full shrink-0 ${t.kind === 'crit' ? 'bg-crit' : t.kind === 'info' ? 'bg-info' : 'bg-ok'}`} />
          <span className="flex-1 min-w-0">{t.text}</span>
          {t.action && <button className="font-head font-bold text-accent" onClick={() => { t.action?.run(); dismiss(t.id); }}>{t.action.label}</button>}
          {t.href && <Link className="font-head font-bold text-accent shrink-0" to={t.href} onClick={() => dismiss(t.id)}>{t.hrefLabel ?? 'Open'}</Link>}
          <button className="text-bg/60 shrink-0 -mr-1 px-1" aria-label="Dismiss" onClick={() => dismiss(t.id)}>×</button>
        </div>
      ))}
    </div>
  );
}
